import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTier } from "@/context/TierContext";
import { CheckCircle2, Loader2 } from "lucide-react";

// After Stripe checkout the subscription webhook may take a moment to land, so we
// poll /billing/status until the tier flips to premium (up to ~20s) before celebrating.
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const { refresh } = useTier();
  const [confirmed, setConfirmed] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const clearAll = () => { timers.current.forEach(clearTimeout); timers.current = []; };

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      const data = await refresh();
      if (cancelled) return;
      if (data?.tier === "premium") {
        setIsTrial(!!data.trialing);
        setConfirmed(true);
        timers.current.push(setTimeout(() => navigate("/dashboard", { replace: true }), 2200));
        return;
      }
      if (attempts >= MAX_ATTEMPTS) {
        // Payment succeeded but the webhook hasn't synced yet — send them onward anyway.
        setTimedOut(true);
        timers.current.push(setTimeout(() => navigate("/dashboard", { replace: true }), 2500));
        return;
      }
      timers.current.push(setTimeout(poll, POLL_INTERVAL_MS));
    };

    poll();
    return () => { cancelled = true; clearAll(); };
  }, [refresh, navigate]);

  return (
    <div className="min-h-screen bg-[#EEF2F6] flex items-center justify-center px-6" data-testid="billing-success">
      <div className="text-center">
        {confirmed ? (
          <>
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-2xl bg-[#DCFCE7] text-[#16A34A]" data-testid="billing-success-confirmed">
              <CheckCircle2 size={34} />
            </div>
            <h1 className="mt-5 font-heading text-3xl font-bold text-navy">{isTrial ? "Your free TenantSeal Plus trial has started 🎉" : "You're on TenantSeal Plus 🎉"}</h1>
            <p className="mt-2 text-[#475569]">Thanks for upgrading. Taking you to your dashboard…</p>
          </>
        ) : timedOut ? (
          <>
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-2xl bg-[#DCFCE7] text-[#16A34A]">
              <CheckCircle2 size={34} />
            </div>
            <h1 className="mt-5 font-heading text-3xl font-bold text-navy">Payment received 🎉</h1>
            <p className="mt-2 max-w-sm text-[#475569]" data-testid="billing-success-pending">
              Your upgrade is being finalized and will appear in a moment. Taking you to your dashboard…
            </p>
          </>
        ) : (
          <>
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-2xl bg-white text-navy shadow-sm" data-testid="billing-success-confirming">
              <Loader2 size={34} className="animate-spin" />
            </div>
            <h1 className="mt-5 font-heading text-3xl font-bold text-navy">Confirming your upgrade…</h1>
            <p className="mt-2 text-[#475569]">Hang tight while we activate your TenantSeal Plus account.</p>
          </>
        )}
      </div>
    </div>
  );
}
